import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add as AddIcon,
  CheckCircle as ApproveIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

import {
  purchaseOrdersApi,
} from "../api/purchaseOrdersApi";

import {
  suppliersApi,
} from "../api/suppliersApi";

import {
  warehousesApi,
} from "../api/warehousesApi";

import {
  productsApi,
} from "../api/productsApi";

import type {
  CreatePurchaseOrderInput,
  PurchaseOrderLineInput,
  PurchaseOrderRecord,
  PurchaseOrderStatus,
} from "../types/purchase-orders.types";

import type {
  SupplierRecord,
} from "../types/suppliers.types";

import type {
  WarehouseRecord,
} from "../types/warehouses.types";

import type {
  AdminProduct,
} from "../types/products.types";

import "../screenstyles/PurchaseOrders.css";


type FormLine = {
  key: string;

  product: AdminProduct | null;

  quantity: number;

  unitPrice: number;

  discountPercentage: number;

  taxPercentage: number;

  notes: string;
};


type PurchaseOrderForm = {
  supplier: SupplierRecord | null;

  warehouse: WarehouseRecord | null;

  currencyCode: string;

  baseCurrencyCode: string;

  exchangeRate: number;

  orderDate: string;

  expectedDeliveryDate: string;

  referenceNumber: string;

  notes: string;

  additionalExpenses: number;

  items: FormLine[];
};


const STATUS_LABELS: Record<
  PurchaseOrderStatus,
  string
> = {
  draft: "مسودة",
  approved: "معتمد",
  partially_received:
    "مستلم جزئياً",
  received: "مستلم",
  cancelled: "ملغي",
};


const CURRENCIES = [
  "IQD",
  "USD",
  "EUR",
  "GBP",
  "AED",
  "SAR",
  "TRY",
];


function todayString(): string {
  const now = new Date();

  const year =
    now.getFullYear();

  const month =
    String(
      now.getMonth() + 1,
    ).padStart(2, "0");

  const day =
    String(
      now.getDate(),
    ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}


function createEmptyLine(): FormLine {
  return {
    key:
      `${Date.now()}-${Math.random()}`,

    product: null,

    quantity: 1,

    unitPrice: 0,

    discountPercentage: 0,

    taxPercentage: 0,

    notes: "",
  };
}


function createEmptyForm(): PurchaseOrderForm {
  return {
    supplier: null,

    warehouse: null,

    currencyCode: "IQD",

    baseCurrencyCode: "IQD",

    exchangeRate: 1,

    orderDate:
      todayString(),

    expectedDeliveryDate: "",

    referenceNumber: "",

    notes: "",

    additionalExpenses: 0,

    items: [
      createEmptyLine(),
    ],
  };
}


function toNumber(
  value: unknown,
): number {
  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
}


function roundMoney(
  value: number,
): number {
  return (
    Math.round(
      (value + Number.EPSILON) *
        1000000,
    ) / 1000000
  );
}


function calculateLine(
  line: FormLine,
) {
  const quantity =
    toNumber(
      line.quantity,
    );

  const unitPrice =
    toNumber(
      line.unitPrice,
    );

  const discountPercentage =
    toNumber(
      line.discountPercentage,
    );

  const taxPercentage =
    toNumber(
      line.taxPercentage,
    );

  const gross =
    roundMoney(
      quantity * unitPrice,
    );

  const discount =
    roundMoney(
      gross *
        (
          discountPercentage /
          100
        ),
    );

  const taxable =
    roundMoney(
      gross - discount,
    );

  const tax =
    roundMoney(
      taxable *
        (
          taxPercentage /
          100
        ),
    );

  const total =
    roundMoney(
      taxable + tax,
    );

  return {
    gross,
    discount,
    taxable,
    tax,
    total,
  };
}


function formatNumber(
  value: number,
): string {
  return new Intl.NumberFormat(
    "en-US",
    {
      maximumFractionDigits: 6,
    },
  ).format(value);
}


function getErrorMessage(
  error: unknown,
): string {
  if (
    typeof error === "object" &&
    error !== null
  ) {
    const candidate =
      error as {
        message?: unknown;

        response?: {
          data?: {
            message?: unknown;

            error?: {
              message?: unknown;
            };
          };
        };
      };

    const responseMessage =
      candidate.response?.data
        ?.error?.message ??
      candidate.response?.data
        ?.message;

    if (
      typeof responseMessage ===
      "string"
    ) {
      return responseMessage;
    }

    if (
      typeof candidate.message ===
      "string"
    ) {
      return candidate.message;
    }
  }

  return "حدث خطأ غير متوقع.";
}


function statusClass(
  status: PurchaseOrderStatus,
): string {
  return (
    `purchase-orders-status ` +
    `purchase-orders-status--${status}`
  );
}


export default function PurchaseOrders() {
  const [
    purchaseOrders,
    setPurchaseOrders,
  ] = useState<
    PurchaseOrderRecord[]
  >([]);

  const [
    suppliers,
    setSuppliers,
  ] = useState<
    SupplierRecord[]
  >([]);

  const [
    warehouses,
    setWarehouses,
  ] = useState<
    WarehouseRecord[]
  >([]);

  const [
    products,
    setProducts,
  ] = useState<
    AdminProduct[]
  >([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState<
    "" | PurchaseOrderStatus
  >("");

  const [
    supplierFilter,
    setSupplierFilter,
  ] = useState("");

  const [
    warehouseFilter,
    setWarehouseFilter,
  ] = useState("");

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    formOpen,
    setFormOpen,
  ] = useState(false);

  const [
    detailsOpen,
    setDetailsOpen,
  ] = useState(false);

  const [
    selectedOrder,
    setSelectedOrder,
  ] = useState<
    PurchaseOrderRecord | null
  >(null);

  const [
    editingOrder,
    setEditingOrder,
  ] = useState<
    PurchaseOrderRecord | null
  >(null);

  const [
    form,
    setForm,
  ] = useState<
    PurchaseOrderForm
  >(
    createEmptyForm(),
  );

  const [
  approveDialogOpen,
  setApproveDialogOpen,
] = useState(false);

const [
  cancelDialogOpen,
  setCancelDialogOpen,
] = useState(false);

const [
  actionOrder,
  setActionOrder,
] = useState<
  PurchaseOrderRecord | null
>(null);

const [
  cancelReason,
  setCancelReason,
] = useState("");

const [
  actionLoading,
  setActionLoading,
] = useState(false);

const [
  actionError,
  setActionError,
] = useState("");


  const loadReferenceData =
    useCallback(
      async () => {
        const [
          suppliersResult,
          warehousesResult,
          productsResult,
        ] =
          await Promise.all([
            suppliersApi.list({
              isActive: true,
              limit: 100,
              sort: "name_asc",
            }),

            warehousesApi.list({
              isActive: true,
              limit: 100,
              sort: "name_asc",
            }),

            productsApi.adminList({
              isActive: true,
              isDeleted: false,
              limit: 100,
              sort:
                "createdAt_desc",
            }),
          ]);

        setSuppliers(
          suppliersResult.items,
        );

        setWarehouses(
          warehousesResult.items,
        );

        setProducts(
          productsResult.items,
        );
      },
      [],
    );


  const loadPurchaseOrders =
    useCallback(
      async () => {
        setLoading(true);

        setError("");

        try {
          const result =
            await purchaseOrdersApi.list(
              {
                search:
                  search.trim() ||
                  undefined,

                status:
                  statusFilter ||
                  undefined,

                supplierId:
                  supplierFilter ||
                  undefined,

                warehouseId:
                  warehouseFilter ||
                  undefined,

                fromDate:
                  fromDate ||
                  undefined,

                toDate:
                  toDate ||
                  undefined,

                sort:
                  "orderDate_desc",

                limit: 100,
              },
            );

          setPurchaseOrders(
            result.items,
          );
        } catch (
          loadError
        ) {
          setError(
            getErrorMessage(
              loadError,
            ),
          );
        } finally {
          setLoading(false);
        }
      },
      [
        search,
        statusFilter,
        supplierFilter,
        warehouseFilter,
        fromDate,
        toDate,
      ],
    );


  useEffect(
    () => {
      void loadReferenceData()
        .catch(
          referenceError => {
            setError(
              getErrorMessage(
                referenceError,
              ),
            );
          },
        );
    },
    [
      loadReferenceData,
    ],
  );


  useEffect(
    () => {
      const timer =
        window.setTimeout(
          () => {
            void loadPurchaseOrders();
          },
          250,
        );

      return () =>
        window.clearTimeout(
          timer,
        );
    },
    [
      loadPurchaseOrders,
    ],
  );


  const calculatedTotals =
    useMemo(
      () => {
        let subtotal = 0;
        let discount = 0;
        let tax = 0;
        let linesTotal = 0;

        form.items.forEach(
          line => {
            const current =
              calculateLine(
                line,
              );

            subtotal +=
              current.gross;

            discount +=
              current.discount;

            tax +=
              current.tax;

            linesTotal +=
              current.total;
          },
        );

        const additionalExpenses =
          toNumber(
            form.additionalExpenses,
          );

        const grandTotal =
          roundMoney(
            linesTotal +
              additionalExpenses,
          );

        return {
          subtotal:
            roundMoney(
              subtotal,
            ),

          discount:
            roundMoney(
              discount,
            ),

          tax:
            roundMoney(tax),

          additionalExpenses,

          grandTotal,

          baseGrandTotal:
            roundMoney(
              grandTotal *
                toNumber(
                  form.exchangeRate,
                ),
            ),
        };
      },
      [form],
    );


  function openCreateDialog() {
    setEditingOrder(null);

    setForm(
      createEmptyForm(),
    );

    setError("");

    setFormOpen(true);
  }


  function openEditDialog(
    order: PurchaseOrderRecord,
  ) {
    const supplier =
      suppliers.find(
        item =>
          item.id ===
          order.supplierId,
      ) ?? null;

    const warehouse =
      warehouses.find(
        item =>
          item.id ===
          order.warehouseId,
      ) ?? null;

    const lines: FormLine[] =
      order.items.map(
        (
          item,
          index,
        ) => {
          const existingProduct =
            products.find(
              product =>
                product.id ===
                item.productId,
            );

          const fallbackProduct:
            AdminProduct = {
            id:
              item.productId,

            name:
              item.productName,

            code:
              item.productCode,

            showStatus: true,

            showStutas: true,

            isActive: true,

            isDeleted: false,

            searchEnabled: true,
          };

          return {
            key:
              item.lineId ||
              `${Date.now()}-${index}`,

            product:
              existingProduct ??
              fallbackProduct,

            quantity:
              item.quantity,

            unitPrice:
              item.unitPrice,

            discountPercentage:
              item.discountPercentage,

            taxPercentage:
              item.taxPercentage,

            notes:
              item.notes ?? "",
          };
        },
      );

    setEditingOrder(order);

    setForm({
      supplier,

      warehouse,

      currencyCode:
        order.currencyCode,

      baseCurrencyCode:
        order.baseCurrencyCode,

      exchangeRate:
        order.exchangeRate,

      orderDate:
        order.orderDate,

      expectedDeliveryDate:
        order.expectedDeliveryDate ??
        "",

      referenceNumber:
        order.referenceNumber ??
        "",

      notes:
        order.notes ?? "",

      additionalExpenses:
        order.additionalExpenses,

      items:
        lines.length > 0
          ? lines
          : [
              createEmptyLine(),
            ],
    });

    setError("");

    setFormOpen(true);
  }


  function updateLine(
    index: number,
    patch: Partial<FormLine>,
  ) {
    setForm(
      current => ({
        ...current,

        items:
          current.items.map(
            (
              line,
              lineIndex,
            ) =>
              lineIndex ===
              index
                ? {
                    ...line,
                    ...patch,
                  }
                : line,
          ),
      }),
    );
  }


  function addLine() {
    setForm(
      current => ({
        ...current,

        items: [
          ...current.items,
          createEmptyLine(),
        ],
      }),
    );
  }


  function removeLine(
    index: number,
  ) {
    setForm(
      current => {
        if (
          current.items.length <=
          1
        ) {
          return current;
        }

        return {
          ...current,

          items:
            current.items.filter(
              (
                _,
                lineIndex,
              ) =>
                lineIndex !==
                index,
            ),
        };
      },
    );
  }


  function validateForm():
    string | null {
    if (!form.supplier) {
      return "يرجى اختيار المورد.";
    }

    if (!form.warehouse) {
      return "يرجى اختيار مخزن الاستلام.";
    }

    if (!form.orderDate) {
      return "يرجى تحديد تاريخ أمر الشراء.";
    }

    if (
      form.expectedDeliveryDate &&
      form.expectedDeliveryDate <
        form.orderDate
    ) {
      return "تاريخ التسليم المتوقع لا يمكن أن يكون قبل تاريخ أمر الشراء.";
    }

    if (
      !form.currencyCode
    ) {
      return "يرجى تحديد العملة.";
    }

    if (
      toNumber(
        form.exchangeRate,
      ) <= 0
    ) {
      return "سعر الصرف يجب أن يكون أكبر من صفر.";
    }

    if (
      form.currencyCode ===
        form.baseCurrencyCode &&
      toNumber(
        form.exchangeRate,
      ) !== 1
    ) {
      return "عندما تكون العملة هي نفس العملة الأساسية يجب أن يكون سعر الصرف 1.";
    }

    const productIds =
      form.items.map(
        line =>
          line.product?.id ??
          "",
      );

    if (
      productIds.some(
        id => !id,
      )
    ) {
      return "يرجى اختيار المادة في جميع السطور.";
    }

    if (
      new Set(
        productIds,
      ).size !==
      productIds.length
    ) {
      return "لا يمكن إضافة نفس المادة أكثر من مرة.";
    }

    for (
      const line of
      form.items
    ) {
      if (
        toNumber(
          line.quantity,
        ) <= 0
      ) {
        return "الكمية يجب أن تكون أكبر من صفر.";
      }

      if (
        toNumber(
          line.unitPrice,
        ) <= 0
      ) {
        return "سعر الشراء يجب أن يكون أكبر من صفر.";
      }

      if (
        line.discountPercentage <
          0 ||
        line.discountPercentage >
          100
      ) {
        return "نسبة الخصم يجب أن تكون بين 0 و100.";
      }

      if (
        line.taxPercentage < 0 ||
        line.taxPercentage > 100
      ) {
        return "نسبة الضريبة يجب أن تكون بين 0 و100.";
      }
    }

    return null;
  }


  function buildItems():
    PurchaseOrderLineInput[] {
    return form.items.map(
      line => ({
        productId:
          line.product!.id,

        quantity:
          toNumber(
            line.quantity,
          ),

        unitPrice:
          toNumber(
            line.unitPrice,
          ),

        discountPercentage:
          toNumber(
            line.discountPercentage,
          ),

        taxPercentage:
          toNumber(
            line.taxPercentage,
          ),

        notes:
          line.notes.trim() ||
          null,
      }),
    );
  }


  async function savePurchaseOrder() {
    const validationError =
      validateForm();

    if (validationError) {
      setError(
        validationError,
      );

      return;
    }

    setSaving(true);

    setError("");

    try {
      const input:
        CreatePurchaseOrderInput = {
        supplierId:
          form.supplier!.id,

        warehouseId:
          form.warehouse!.id,

        currencyCode:
          form.currencyCode,

        baseCurrencyCode:
          form.baseCurrencyCode,

        exchangeRate:
          toNumber(
            form.exchangeRate,
          ),

        orderDate:
          form.orderDate,

        expectedDeliveryDate:
          form.expectedDeliveryDate ||
          null,

        referenceNumber:
          form.referenceNumber.trim() ||
          null,

        notes:
          form.notes.trim() ||
          null,

        additionalExpenses:
          toNumber(
            form.additionalExpenses,
          ),

        items:
          buildItems(),
      };

      if (editingOrder) {
        await purchaseOrdersApi.update(
          editingOrder.purchaseOrderId,
          input,
        );
      } else {
        await purchaseOrdersApi.create(
          input,
        );
      }

      setFormOpen(false);

      setEditingOrder(null);

      await loadPurchaseOrders();
    } catch (
      saveError
    ) {
      setError(
        getErrorMessage(
          saveError,
        ),
      );
    } finally {
      setSaving(false);
    }
  }


  async function viewOrder(
    order: PurchaseOrderRecord,
  ) {
    setError("");

    try {
      const result =
        await purchaseOrdersApi.getById(
          order.purchaseOrderId,
        );

      setSelectedOrder(
        result.purchaseOrder,
      );

      setDetailsOpen(true);
    } catch (
      detailsError
    ) {
      setError(
        getErrorMessage(
          detailsError,
        ),
      );
    }
  }




 function approveOrder(
  order: PurchaseOrderRecord,
) {
  setActionOrder(order);
  setActionError("");
  setApproveDialogOpen(true);
}

async function confirmApproveOrder() {
  if (!actionOrder) {
    return;
  }

  setActionLoading(true);
  setActionError("");

  try {
    await purchaseOrdersApi.approve(
      actionOrder.purchaseOrderId,
    );

    setApproveDialogOpen(false);
    setActionOrder(null);

    await loadPurchaseOrders();
  } catch (approveError) {
    setActionError(
      getErrorMessage(
        approveError,
      ),
    );
  } finally {
    setActionLoading(false);
  }
}

function cancelOrder(
  order: PurchaseOrderRecord,
) {
  setActionOrder(order);
  setCancelReason("");
  setActionError("");
  setCancelDialogOpen(true);
}

async function confirmCancelOrder() {
  if (!actionOrder) {
    return;
  }

  const reason =
    cancelReason.trim();

  if (!reason) {
    setActionError(
      "سبب الإلغاء مطلوب.",
    );

    return;
  }

  setActionLoading(true);
  setActionError("");

  try {
    await purchaseOrdersApi.cancel(
      actionOrder.purchaseOrderId,
      {
        reason,
      },
    );

    setCancelDialogOpen(false);
    setActionOrder(null);
    setCancelReason("");

    await loadPurchaseOrders();
  } catch (cancelError) {
    setActionError(
      getErrorMessage(
        cancelError,
      ),
    );
  } finally {
    setActionLoading(false);
  }
}


  return (
    <Box
      className="purchase-orders-page"
      dir="rtl"
    >
      <Box className="purchase-orders-header">
        <Box>
          <Typography
            variant="h4"
            className="purchase-orders-title"
          >
            أوامر الشراء
          </Typography>

          <Typography className="purchase-orders-subtitle">
            إدارة أوامر شراء الموردين ومتابعة الاعتماد والاستلام
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1}
        >
          <Tooltip title="تحديث">
            <IconButton
              onClick={() =>
                void loadPurchaseOrders()
              }
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={
              <AddIcon />
            }
            onClick={
              openCreateDialog
            }
          >
            إضافة أمر شراء
          </Button>
        </Stack>
      </Box>


      {error && (
        <Alert
          severity="error"
          onClose={() =>
            setError("")
          }
          sx={{
            mb: 2,
          }}
        >
          {error}
        </Alert>
      )}


      <Paper className="purchase-orders-filters">
        <TextField
          label="بحث"
          size="small"
          value={search}
          onChange={
            event =>
              setSearch(
                event.target.value,
              )
          }
          placeholder="رقم الأمر أو المورد"
        />

        <TextField
          select
          label="الحالة"
          size="small"
          value={
            statusFilter
          }
          onChange={
            event =>
              setStatusFilter(
                event.target.value as
                  | ""
                  | PurchaseOrderStatus,
              )
          }
        >
          <MenuItem value="">
            جميع الحالات
          </MenuItem>

          {(
            Object.keys(
              STATUS_LABELS,
            ) as PurchaseOrderStatus[]
          ).map(
            status => (
              <MenuItem
                key={status}
                value={status}
              >
                {
                  STATUS_LABELS[
                    status
                  ]
                }
              </MenuItem>
            ),
          )}
        </TextField>

        <TextField
          select
          label="المورد"
          size="small"
          value={
            supplierFilter
          }
          onChange={
            event =>
              setSupplierFilter(
                event.target.value,
              )
          }
        >
          <MenuItem value="">
            جميع الموردين
          </MenuItem>

          {suppliers.map(
            supplier => (
              <MenuItem
                key={
                  supplier.id
                }
                value={
                  supplier.id
                }
              >
                {supplier.nameAr ||
                  supplier.name}
              </MenuItem>
            ),
          )}
        </TextField>

        <TextField
          select
          label="المخزن"
          size="small"
          value={
            warehouseFilter
          }
          onChange={
            event =>
              setWarehouseFilter(
                event.target.value,
              )
          }
        >
          <MenuItem value="">
            جميع المخازن
          </MenuItem>

          {warehouses.map(
            warehouse => (
              <MenuItem
                key={
                  warehouse.id
                }
                value={
                  warehouse.id
                }
              >
                {warehouse.nameAr ||
                  warehouse.name}
              </MenuItem>
            ),
          )}
        </TextField>

        <TextField
          label="من تاريخ"
          type="date"
          size="small"
          value={fromDate}
          onChange={
            event =>
              setFromDate(
                event.target.value,
              )
          }
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          label="إلى تاريخ"
          type="date"
          size="small"
          value={toDate}
          onChange={
            event =>
              setToDate(
                event.target.value,
              )
          }
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Paper>


      <TableContainer
        component={Paper}
        className="purchase-orders-table-container"
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                رقم الأمر
              </TableCell>

              <TableCell>
                التاريخ
              </TableCell>

              <TableCell>
                المورد
              </TableCell>

              <TableCell>
                المخزن
              </TableCell>

              <TableCell>
                العملة
              </TableCell>

              <TableCell>
                الإجمالي
              </TableCell>

              <TableCell>
                الحالة
              </TableCell>

              <TableCell align="center">
                الإجراءات
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                >
                  <Box sx={{ py: 5 }}>
                    <CircularProgress
                      size={30}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ) : purchaseOrders.length ===
              0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                >
                  <Box sx={{ py: 5 }}>
                    لا توجد أوامر شراء
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              purchaseOrders.map(
                order => (
                  <TableRow
                    key={
                      order.purchaseOrderId
                    }
                    hover
                  >
                    <TableCell>
                      <strong>
                        {
                          order.orderNumber
                        }
                      </strong>
                    </TableCell>

                    <TableCell>
                      {
                        order.orderDate
                      }
                    </TableCell>

                    <TableCell>
                      {order.supplierNameAr ||
                        order.supplierName}
                    </TableCell>

                    <TableCell>
                      {order.warehouseNameAr ||
                        order.warehouseName}
                    </TableCell>

                    <TableCell>
                      {
                        order.currencyCode
                      }
                    </TableCell>

                    <TableCell>
                      {formatNumber(
                        order.grandTotal,
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          STATUS_LABELS[
                            order.status
                          ]
                        }
                        className={
                          statusClass(
                            order.status,
                          )
                        }
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <Tooltip title="عرض">
                          <IconButton
                            size="small"
                            onClick={() =>
                              void viewOrder(
                                order,
                              )
                            }
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {order.status ===
                          "draft" && (
                          <>
                            <Tooltip title="تعديل">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openEditDialog(
                                    order,
                                  )
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="اعتماد">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  void approveOrder(
                                    order,
                                  )
                                }
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        {order.status !==
                          "cancelled" &&
                          order.status !==
                            "received" &&
                          order.status !==
                            "partially_received" && (
                            <Tooltip title="إلغاء">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  void cancelOrder(
                                    order,
                                  )
                                }
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ),
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>


     <Dialog
  open={formOpen}
  onClose={() => {
    if (!saving) {
      setFormOpen(false);
    }
  }}
  className="purchase-orders-dialog"
  fullWidth
  maxWidth="xl"
>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <span>
              {editingOrder
                ? `تعديل أمر الشراء ${editingOrder.orderNumber}`
                : "إضافة أمر شراء جديد"}
            </span>

            <IconButton
              onClick={() =>
                setFormOpen(false)
              }
              disabled={saving}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Box className="purchase-orders-form-grid">
            <Autocomplete
              options={suppliers}
              value={form.supplier}
              isOptionEqualToValue={(
                option,
                value,
              ) =>
                option.id ===
                value.id
              }
              getOptionLabel={
                option =>
                  `${
                    option.nameAr ||
                    option.name
                  } (${option.code})`
              }
              onChange={(
                _,
                value,
              ) => {
                setForm(
                  current => {
                    const currency =
                      value?.currencyCode ||
                      current.currencyCode;

                    return {
                      ...current,

                      supplier:
                        value,

                      currencyCode:
                        currency,

                      exchangeRate:
                        currency ===
                        current.baseCurrencyCode
                          ? 1
                          : current.exchangeRate,
                    };
                  },
                );
              }}
              renderInput={
                params => (
                  <TextField
                    {...params}
                    label="المورد *"
                  />
                )
              }
            />

            <Autocomplete
              options={warehouses}
              value={form.warehouse}
              isOptionEqualToValue={(
                option,
                value,
              ) =>
                option.id ===
                value.id
              }
              getOptionLabel={
                option =>
                  `${
                    option.nameAr ||
                    option.name
                  } (${option.code})`
              }
              onChange={(
                _,
                value,
              ) =>
                setForm(
                  current => ({
                    ...current,

                    warehouse:
                      value,
                  }),
                )
              }
              renderInput={
                params => (
                  <TextField
                    {...params}
                    label="مخزن الاستلام *"
                  />
                )
              }
            />

            <TextField
              label="تاريخ الأمر *"
              type="date"
              value={
                form.orderDate
              }
              onChange={
                event =>
                  setForm(
                    current => ({
                      ...current,

                      orderDate:
                        event.target.value,
                    }),
                  )
              }
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="تاريخ التسليم المتوقع"
              type="date"
              value={
                form.expectedDeliveryDate
              }
              onChange={
                event =>
                  setForm(
                    current => ({
                      ...current,

                      expectedDeliveryDate:
                        event.target.value,
                    }),
                  )
              }
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              select
              label="العملة *"
              value={
                form.currencyCode
              }
              onChange={
                event => {
                  const currency =
                    event.target.value;

                  setForm(
                    current => ({
                      ...current,

                      currencyCode:
                        currency,

                      exchangeRate:
                        currency ===
                        current.baseCurrencyCode
                          ? 1
                          : current.exchangeRate,
                    }),
                  );
                }
              }
            >
              {CURRENCIES.map(
                currency => (
                  <MenuItem
                    key={currency}
                    value={currency}
                  >
                    {currency}
                  </MenuItem>
                ),
              )}
            </TextField>

            <TextField
              label="العملة الأساسية"
              value={
                form.baseCurrencyCode
              }
              disabled
            />

            <TextField
              label="سعر الصرف *"
              type="number"
              value={
                form.exchangeRate
              }
              disabled={
                form.currencyCode ===
                form.baseCurrencyCode
              }
              onChange={
                event =>
                  setForm(
                    current => ({
                      ...current,

                      exchangeRate:
                        toNumber(
                          event.target.value,
                        ),
                    }),
                  )
              }
              inputProps={{
                min: 0,
                step: "any",
              }}
            />

            <TextField
              label="الرقم المرجعي"
              value={
                form.referenceNumber
              }
              onChange={
                event =>
                  setForm(
                    current => ({
                      ...current,

                      referenceNumber:
                        event.target.value,
                    }),
                  )
              }
            />

            <TextField
              label="مصاريف إضافية"
              type="number"
              value={
                form.additionalExpenses
              }
              onChange={
                event =>
                  setForm(
                    current => ({
                      ...current,

                      additionalExpenses:
                        toNumber(
                          event.target.value,
                        ),
                    }),
                  )
              }
              inputProps={{
                min: 0,
                step: "any",
              }}
            />

            <TextField
              label="ملاحظات"
              value={form.notes}
              onChange={
                event =>
                  setForm(
                    current => ({
                      ...current,

                      notes:
                        event.target.value,
                    }),
                  )
              }
              multiline
              minRows={2}
            />
          </Box>


          <Divider sx={{ my: 3 }} />


          <Box className="purchase-orders-items-header">
            <Typography variant="h6">
              مواد أمر الشراء
            </Typography>

            <Button
              variant="outlined"
              startIcon={
                <AddIcon />
              }
              onClick={addLine}
            >
              إضافة مادة
            </Button>
          </Box>


          <TableContainer className="purchase-orders-lines-table">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    المادة
                  </TableCell>

                  <TableCell>
                    الكمية
                  </TableCell>

                  <TableCell>
                    سعر الشراء
                  </TableCell>

                  <TableCell>
                    الخصم %
                  </TableCell>

                  <TableCell>
                    الضريبة %
                  </TableCell>

                  <TableCell>
                    الإجمالي
                  </TableCell>

                  <TableCell>
                    ملاحظات
                  </TableCell>

                  <TableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {form.items.map(
                  (
                    line,
                    index,
                  ) => {
                    const totals =
                      calculateLine(
                        line,
                      );

                    return (
                      <TableRow
                        key={line.key}
                      >
                        <TableCell className="purchase-orders-product-cell">
                          <Autocomplete
                            options={
                              products
                            }
                            value={
                              line.product
                            }
                            isOptionEqualToValue={(
                              option,
                              value,
                            ) =>
                              option.id ===
                              value.id
                            }
                            getOptionLabel={
                              option =>
                                `${option.name} (${option.code})`
                            }
                            onChange={(
                              _,
                              value,
                            ) =>
                              updateLine(
                                index,
                                {
                                  product:
                                    value,
                                },
                              )
                            }
                            renderInput={
                              params => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder="اختر المادة"
                                />
                              )
                            }
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={
                              line.quantity
                            }
                            onChange={
                              event =>
                                updateLine(
                                  index,
                                  {
                                    quantity:
                                      toNumber(
                                        event.target.value,
                                      ),
                                  },
                                )
                            }
                            inputProps={{
                              min: 0,
                              step: "any",
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={
                              line.unitPrice
                            }
                            onChange={
                              event =>
                                updateLine(
                                  index,
                                  {
                                    unitPrice:
                                      toNumber(
                                        event.target.value,
                                      ),
                                  },
                                )
                            }
                            inputProps={{
                              min: 0,
                              step: "any",
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={
                              line.discountPercentage
                            }
                            onChange={
                              event =>
                                updateLine(
                                  index,
                                  {
                                    discountPercentage:
                                      toNumber(
                                        event.target.value,
                                      ),
                                  },
                                )
                            }
                            inputProps={{
                              min: 0,
                              max: 100,
                              step: "any",
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={
                              line.taxPercentage
                            }
                            onChange={
                              event =>
                                updateLine(
                                  index,
                                  {
                                    taxPercentage:
                                      toNumber(
                                        event.target.value,
                                      ),
                                  },
                                )
                            }
                            inputProps={{
                              min: 0,
                              max: 100,
                              step: "any",
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <strong>
                            {formatNumber(
                              totals.total,
                            )}
                          </strong>
                        </TableCell>

                        <TableCell>
                          <TextField
                            size="small"
                            value={
                              line.notes
                            }
                            onChange={
                              event =>
                                updateLine(
                                  index,
                                  {
                                    notes:
                                      event.target.value,
                                  },
                                )
                            }
                          />
                        </TableCell>

                        <TableCell>
                          <Tooltip title="حذف السطر">
                            <span>
                              <IconButton
                                size="small"
                                disabled={
                                  form.items.length <=
                                  1
                                }
                                onClick={() =>
                                  removeLine(
                                    index,
                                  )
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  },
                )}
              </TableBody>
            </Table>
          </TableContainer>


          <Box className="purchase-orders-totals">
            <Box>
              <span>
                المجموع قبل الخصم
              </span>

              <strong>
                {formatNumber(
                  calculatedTotals.subtotal,
                )}
              </strong>
            </Box>

            <Box>
              <span>
                إجمالي الخصم
              </span>

              <strong>
                {formatNumber(
                  calculatedTotals.discount,
                )}
              </strong>
            </Box>

            <Box>
              <span>
                إجمالي الضريبة
              </span>

              <strong>
                {formatNumber(
                  calculatedTotals.tax,
                )}
              </strong>
            </Box>

            <Box>
              <span>
                المصاريف الإضافية
              </span>

              <strong>
                {formatNumber(
                  calculatedTotals.additionalExpenses,
                )}
              </strong>
            </Box>

            <Box className="purchase-orders-grand-total">
              <span>
                الإجمالي النهائي
              </span>

              <strong>
                {formatNumber(
                  calculatedTotals.grandTotal,
                )}{" "}
                {form.currencyCode}
              </strong>
            </Box>

            {form.currencyCode !==
              form.baseCurrencyCode && (
              <Box>
                <span>
                  الإجمالي بالعملة الأساسية
                </span>

                <strong>
                  {formatNumber(
                    calculatedTotals.baseGrandTotal,
                  )}{" "}
                  {
                    form.baseCurrencyCode
                  }
                </strong>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setFormOpen(false)
            }
            disabled={saving}
          >
            إلغاء
          </Button>

          <Button
            variant="contained"
            disabled={saving}
            onClick={() =>
              void savePurchaseOrder()
            }
          >
            {saving
              ? "جاري الحفظ..."
              : editingOrder
                ? "حفظ التعديلات"
                : "إنشاء أمر الشراء"}
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog
  open={detailsOpen}
  onClose={() =>
    setDetailsOpen(false)
  }
  className="purchase-orders-dialog"
  fullWidth
  maxWidth="lg"
>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <span>
              تفاصيل أمر الشراء
              {selectedOrder
                ? ` - ${selectedOrder.orderNumber}`
                : ""}
            </span>

            <IconButton
              onClick={() =>
                setDetailsOpen(false)
              }
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {selectedOrder && (
            <>
              <Box className="purchase-orders-details-grid">
                <Box>
                  <span>المورد</span>

                  <strong>
                    {selectedOrder.supplierNameAr ||
                      selectedOrder.supplierName}
                  </strong>
                </Box>

                <Box>
                  <span>المخزن</span>

                  <strong>
                    {selectedOrder.warehouseNameAr ||
                      selectedOrder.warehouseName}
                  </strong>
                </Box>

                <Box>
                  <span>
                    تاريخ الأمر
                  </span>

                  <strong>
                    {
                      selectedOrder.orderDate
                    }
                  </strong>
                </Box>

                <Box>
                  <span>
                    التسليم المتوقع
                  </span>

                  <strong>
                    {selectedOrder.expectedDeliveryDate ||
                      "-"}
                  </strong>
                </Box>

                <Box>
                  <span>العملة</span>

                  <strong>
                    {
                      selectedOrder.currencyCode
                    }
                  </strong>
                </Box>

                <Box>
                  <span>
                    سعر الصرف
                  </span>

                  <strong>
                    {formatNumber(
                      selectedOrder.exchangeRate,
                    )}
                  </strong>
                </Box>

                <Box>
                  <span>الحالة</span>

                  <Chip
                    size="small"
                    label={
                      STATUS_LABELS[
                        selectedOrder.status
                      ]
                    }
                    className={
                      statusClass(
                        selectedOrder.status,
                      )
                    }
                  />
                </Box>

                <Box>
                  <span>
                    الرقم المرجعي
                  </span>

                  <strong>
                    {selectedOrder.referenceNumber ||
                      "-"}
                  </strong>
                </Box>
              </Box>


              <Divider sx={{ my: 3 }} />


              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        الكود
                      </TableCell>

                      <TableCell>
                        المادة
                      </TableCell>

                      <TableCell>
                        الكمية
                      </TableCell>

                      <TableCell>
                        المستلم
                      </TableCell>

                      <TableCell>
                        المتبقي
                      </TableCell>

                      <TableCell>
                        سعر الشراء
                      </TableCell>

                      <TableCell>
                        الخصم
                      </TableCell>

                      <TableCell>
                        الضريبة
                      </TableCell>

                      <TableCell>
                        الإجمالي
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {selectedOrder.items.map(
                      item => (
                        <TableRow
                          key={
                            item.lineId
                          }
                        >
                          <TableCell>
                            {
                              item.productCode
                            }
                          </TableCell>

                          <TableCell>
                            {
                              item.productName
                            }
                          </TableCell>

                          <TableCell>
                            {
                              item.quantity
                            }
                          </TableCell>

                          <TableCell>
                            {
                              item.receivedQuantity
                            }
                          </TableCell>

                          <TableCell>
                            {
                              item.remainingQuantity
                            }
                          </TableCell>

                          <TableCell>
                            {formatNumber(
                              item.unitPrice,
                            )}
                          </TableCell>

                          <TableCell>
                            {
                              item.discountPercentage
                            }
                            %
                          </TableCell>

                          <TableCell>
                            {
                              item.taxPercentage
                            }
                            %
                          </TableCell>

                          <TableCell>
                            {formatNumber(
                              item.lineTotal,
                            )}
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>


              <Box className="purchase-orders-totals purchase-orders-details-totals">
                <Box>
                  <span>
                    المجموع
                  </span>

                  <strong>
                    {formatNumber(
                      selectedOrder.subtotal,
                    )}
                  </strong>
                </Box>

                <Box>
                  <span>
                    الخصم
                  </span>

                  <strong>
                    {formatNumber(
                      selectedOrder.discountTotal,
                    )}
                  </strong>
                </Box>

                <Box>
                  <span>
                    الضريبة
                  </span>

                  <strong>
                    {formatNumber(
                      selectedOrder.taxTotal,
                    )}
                  </strong>
                </Box>

                <Box>
                  <span>
                    المصاريف الإضافية
                  </span>

                  <strong>
                    {formatNumber(
                      selectedOrder.additionalExpenses,
                    )}
                  </strong>
                </Box>

                <Box className="purchase-orders-grand-total">
                  <span>
                    الإجمالي النهائي
                  </span>

                  <strong>
                    {formatNumber(
                      selectedOrder.grandTotal,
                    )}{" "}
                    {
                      selectedOrder.currencyCode
                    }
                  </strong>
                </Box>
              </Box>


              {selectedOrder.notes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2">
                    الملاحظات
                  </Typography>

                  <Typography>
                    {
                      selectedOrder.notes
                    }
                  </Typography>
                </Box>
              )}


              {selectedOrder.cancellationReason && (
                <Alert
                  severity="warning"
                  sx={{ mt: 3 }}
                >
                  سبب الإلغاء:{" "}
                  {
                    selectedOrder.cancellationReason
                  }
                </Alert>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* =========================================
    Approve Purchase Order Dialog
========================================= */}
<Dialog
  open={approveDialogOpen}
  onClose={() => {
    if (!actionLoading) {
      setApproveDialogOpen(false);
      setActionOrder(null);
      setActionError("");
    }
  }}
  fullWidth
  maxWidth="sm"
  dir="rtl"
>
  <DialogTitle>
    اعتماد أمر الشراء
  </DialogTitle>

  <DialogContent dividers>
    <Stack spacing={2}>
      <Typography>
        هل أنت متأكد من اعتماد أمر الشراء
        {" "}
        <strong>
          {actionOrder?.orderNumber}
        </strong>
        ؟
      </Typography>

      <Alert severity="warning">
        بعد اعتماد أمر الشراء لن يكون من الممكن
        تعديل بياناته كمسودة.
      </Alert>

      {actionError && (
        <Alert severity="error">
          {actionError}
        </Alert>
      )}
    </Stack>
  </DialogContent>

  <DialogActions>
    <Button
      onClick={() => {
        setApproveDialogOpen(false);
        setActionOrder(null);
        setActionError("");
      }}
      disabled={actionLoading}
    >
      تراجع
    </Button>

    <Button
      variant="contained"
      startIcon={
        actionLoading
          ? (
            <CircularProgress
              size={18}
              color="inherit"
            />
          )
          : <ApproveIcon />
      }
      onClick={() =>
        void confirmApproveOrder()
      }
      disabled={actionLoading}
    >
      {actionLoading
        ? "جاري الاعتماد..."
        : "تأكيد الاعتماد"}
    </Button>
  </DialogActions>
</Dialog>


{/* =========================================
    Cancel Purchase Order Dialog
========================================= */}
<Dialog
  open={cancelDialogOpen}
  onClose={() => {
    if (!actionLoading) {
      setCancelDialogOpen(false);
      setActionOrder(null);
      setCancelReason("");
      setActionError("");
    }
  }}
  fullWidth
  maxWidth="sm"
  dir="rtl"
>
  <DialogTitle>
    إلغاء أمر الشراء
  </DialogTitle>

  <DialogContent dividers>
    <Stack spacing={2}>
      <Typography>
        أنت على وشك إلغاء أمر الشراء
        {" "}
        <strong>
          {actionOrder?.orderNumber}
        </strong>
      </Typography>

      <Alert severity="warning">
        سيتم تسجيل أمر الشراء كملغي مع الاحتفاظ
        بسبب الإلغاء في النظام.
      </Alert>

      <TextField
        label="سبب الإلغاء *"
        value={cancelReason}
        onChange={event => {
          setCancelReason(
            event.target.value,
          );

          if (actionError) {
            setActionError("");
          }
        }}
        multiline
        minRows={3}
        fullWidth
        autoFocus
        placeholder="اكتب سبب إلغاء أمر الشراء..."
        disabled={actionLoading}
      />

      {actionError && (
        <Alert severity="error">
          {actionError}
        </Alert>
      )}
    </Stack>
  </DialogContent>

  <DialogActions>
    <Button
      onClick={() => {
        setCancelDialogOpen(false);
        setActionOrder(null);
        setCancelReason("");
        setActionError("");
      }}
      disabled={actionLoading}
    >
      تراجع
    </Button>

    <Button
      variant="contained"
      color="error"
      startIcon={
        actionLoading
          ? (
            <CircularProgress
              size={18}
              color="inherit"
            />
          )
          : <CancelIcon />
      }
      onClick={() =>
        void confirmCancelOrder()
      }
      disabled={actionLoading}
    >
      {actionLoading
        ? "جاري الإلغاء..."
        : "تأكيد الإلغاء"}
    </Button>
  </DialogActions>
</Dialog>

    </Box>
  );
}